'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'
import CorrectionNote from '@/app/components/blog/CorrectionNote'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Do Brita Filters Remove Microplastics? Only the Larger Ones"
      author="MicroPlastics Research Desk"
      date="June 21, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/water.webp"
      heroImageAlt="Do Brita filters remove microplastics. Standard, Elite, ZeroWater and reverse osmosis compared"
    >
      <BlogLastUpdated iso="2026-09-21" formatted="September 21, 2026" />

      <CorrectionNote date="September 21, 2026">
        This page previously said Brita filters are not certified to NSF/ANSI 401 and publish no microplastic figure, and that ZeroWater is. The Brita Elite cartridge is certified to NSF 401 for 99.6% microplastic reduction; ZeroWater makes no microplastic claim. It also called reverse osmosis the gold standard for nanoplastics; no filter of any type is certified for nanoplastics, and most RO models carry no microplastics claim.
      </CorrectionNote>


      <BlogTLDR>
        <strong>It depends on the cartridge.</strong> The{' '}
        <strong>Brita Elite</strong> (the blue filter, formerly LongLast+) is
        certified by WQA to <strong>NSF/ANSI 401</strong> for{' '}
        <strong>99.6% reduction of microplastic particles 0.5–1 µm</strong>,
        and to NSF 53 for lead. The <strong>standard white Brita</strong> is
        certified only for chlorine, taste and zinc (NSF 42) and cadmium,
        copper and mercury (NSF 53): no microplastic claim, and no lead.
        Neither filter is certified for nanoplastics, which no pitcher is. So
        if you own a Brita, the single most useful change is swapping to the
        Elite cartridge. Nanoplastics are a different matter: no filter of any
        type, reverse osmosis included, is certified to remove them.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The <strong>Brita Elite</strong> cartridge is certified under NSF/ANSI 401 for 99.6% reduction of microplastic particles 0.5–1 µm. The standard white cartridge has no microplastic certification.</>,
          <>Standard Brita is certified for chlorine, taste, zinc, cadmium, copper and mercury. Brita Elite adds lead, microplastics and a ~6-month life.</>,
          <>Microplastics span 1&nbsp;µm to 5&nbsp;mm. Loose-granule carbon pitchers catch the coarse end; sub-micron particles and nanoplastics slip through.</>,
          <>ZeroWater is certified for lead and PFAS, not microplastics. No filter of any type is certified for nanoplastics.</>,
          <>The Brita reservoir is BPA-free plastic holding cold water at room temperature, so the pitcher itself sheds little; a glass or stainless dispenser removes even that.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_brita" />

      <KeyStats
        heading="Brita & microplastics, the filtration facts"
        stats={[
          { value: '99.6%', label: 'microplastic reduction, Brita Elite', context: 'particles 0.5–1 µm, certified by WQA to NSF/ANSI 401; the standard filter has no such claim', source: 'Brita Elite performance data sheet', sourceUrl: 'https://www.brita.com/wp-content/uploads/Longlast-PDS-with-401.pdf' },
          { value: '1 µm – 5 mm', label: 'microplastic size range', context: 'pitchers catch the coarse end; the fine end and nanoplastics pass', source: 'WHO 2022 microplastics report', sourceUrl: 'https://www.who.int/publications/i/item/9789240054608' },
          { value: 'carbon + resin', label: 'Brita filter technology', context: 'loose activated-carbon granules + ion-exchange resin, not a dense sub-micron block', source: 'Brita product documentation', sourceUrl: 'https://www.brita.com/' },
          { value: '~6 months', label: 'Brita Elite cartridge life', context: 'about 120 gallons, three times the 2-month standard filter', source: 'Brita Elite performance data sheet', sourceUrl: 'https://www.brita.com/wp-content/uploads/Longlast-PDS-with-401.pdf' },
          { value: 'NSF 401', label: 'the microplastics standard to look for', context: 'the certification that actually includes a microplastics reduction claim', source: 'NSF/ANSI 401 protocol', sourceUrl: 'https://www.nsf.org/knowledge-library/nsf-ansi-401' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How a Brita filter actually works, and where it stops
      </h2>
      <p>
        A Brita pitcher cartridge is two materials doing two jobs. The{' '}
        <strong>activated carbon</strong> adsorbs chlorine and the organic
        compounds that cause off-tastes and odours. The <strong>ion-exchange
        resin</strong> swaps out metal ions, the Elite cartridge is certified
        to reduce lead. Neither of those jobs is &ldquo;remove microplastics.&rdquo;
        What removes microplastics is <strong>physical size exclusion</strong>, and
        the Elite&apos;s denser media is good enough at it to be certified:
        a particle too big to fit through the media gets caught.
      </p>
      <p>
        That is why the answer is &ldquo;partly.&rdquo; Microplastics are not one
        size, they run from 5&nbsp;millimetres all the way down to 1&nbsp;micron,
        and nanoplastics smaller still. A loose-granule carbon pitcher has
        relatively large, uneven flow paths between the granules, so it reliably
        catches the coarse particles and progressively less as the particles get
        smaller. A <strong>dense extruded carbon block</strong>, the kind used
        in filters certified to NSF/ANSI 401, has a much finer, more uniform
        pore structure and captures a far larger share of the small end. Brita&apos;s
        standard cartridge is not that; the Elite is certified to the same
        microplastics standard.
      </p>

      <BlogComparisonTable
        caption="Common household filters vs microplastics (2026)"
        columns={[
          { key: 'filter', label: 'Filter' },
          { key: 'tech', label: 'Technology' },
          { key: 'cert', label: 'Certification' },
          { key: 'mp', label: 'Microplastics' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { filter: 'Brita Standard', tech: 'Carbon granules + resin', cert: 'NSF 42, 53 (cadmium, copper, mercury)', mp: 'No claim; coarse particles only', verdict: 'Taste, not microplastics' },
          { filter: 'Brita Elite / Longlast+', tech: 'Denser carbon + resin', cert: 'NSF 42, 53, 401 (WQA)', mp: '99.6% certified (0.5–1 µm)', verdict: 'Certified for microplastics' },
          { filter: 'ZeroWater (5-stage)', tech: 'Carbon + ion exchange', cert: 'NSF 42, 53 (IAPMO): lead, PFAS', mp: 'No claim', verdict: 'Pick it for lead and PFAS' },
          { filter: 'Clearly Filtered', tech: 'Multi-stage affinity', cert: 'NSF 42, 372 only', mp: '>99.99% in brand lab tests', verdict: 'Strong lab data, not certified' },
          { filter: 'Reverse osmosis', tech: 'Membrane', cert: 'NSF 58 (401 only on some models, e.g. AquaTru)', mp: 'AquaTru 96.48% certified; most RO models no claim', verdict: 'Check the model' },
        ]}
      />
      <p className="text-sm text-gray-500">
        Certifications vary by specific model and are updated over time; check
        the exact SKU on the{' '}
        <a href="https://info.nsf.org/Certified/DWTU/" target="_blank" rel="noopener noreferrer" className="text-[#1E6B47] underline hover:text-[#155235]">NSF certified-products database</a>{' '}
        before buying. For the full pitcher and system breakdown, see our{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] underline hover:text-[#155235]">water-filter comparison</Link>{' '}
        and{' '}
        <Link href="/blog/best-water-filter-pitchers-ranked" className="text-[#1E6B47] underline hover:text-[#155235]">best pitchers ranked</Link>.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>The polymer and packaging of the bottled waters and drinks you scan, so you can see what you&apos;re filtering out, and what you&apos;re re-adding after.</>,
          <>A 0–100 microplastic risk score per product, so &ldquo;filtered tap in a glass&rdquo; can be compared against &ldquo;bottled water in PET.&rdquo;</>,
          <>Lower-plastic swaps for the containers you store and drink filtered water from (glass and steel over plastic).</>,
          <>A running exposure log so you can tell whether switching filters or bottles is actually moving your overall number.</>,
        ]}
      />

      <BlogAppCTA
        headline="Filtering tap is only half the equation"
        sub="A great filter does nothing if you pour the water into a plastic bottle that sheds on the way to work. Scan your bottled waters, your filter pitcher, and your daily bottle to see where the plastic actually re-enters."
        buttonLabel="Scan my water setup"
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        So should you keep your Brita?
      </h2>
      <p>
        For most people, yes, with realistic expectations. A Brita meaningfully
        improves taste, cuts chlorine, and on the Elite cartridge is certified
        to reduce lead and microplastics; the standard cartridge still strips
        out the larger particles as a side effect.
        That is a genuine upgrade over unfiltered tap and far better for the
        planet than bottled water in PET, which carries its own microplastic
        load. Three ways to get more out of it:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Use the Elite cartridge, not the standard.</strong> It is the Brita filter certified for microplastics (99.6%) and lead, and it lasts about six months.</li>
        <li><strong>Change the cartridge on schedule.</strong> A loaded filter channels water around exhausted media and its particulate capture drops. Late changes are the most common reason a filter underperforms.</li>
        <li><strong>Nanoplastics are an open question for every filter.</strong> No filter, pitcher or reverse osmosis, is certified for them, so no upgrade buys certified nanoplastic removal. The{' '}<Link href="/blog/best-water-filter-pitchers-ranked" className="text-forest underline">pitcher ranking</Link>{' '}shows which other pitchers are certified for microplastics and which are only lab-tested.</li>
      </ul>

      <BlogFAQ
        items={[
          {
            question: 'Do Brita filters remove microplastics?',
            answer:
              'It depends on the cartridge. The Brita Elite is certified by WQA to NSF/ANSI 401 for 99.6% reduction of microplastic particles 0.5 to 1 micron. The standard white Brita cartridge has no microplastic certification: it traps some larger particles by size exclusion, but it is certified only for chlorine, taste, zinc, cadmium, copper and mercury. Neither is certified for nanoplastics.',
          },
          {
            question: 'Is Brita Elite better than standard Brita for microplastics?',
            answer:
              'Yes, clearly. The Elite (formerly Longlast+) is certified under NSF/ANSI 401 for 99.6% reduction of microplastic particles 0.5 to 1 micron, and under NSF 53 for lead. The standard cartridge carries neither claim. The Elite also lasts about six months, three times as long.',
          },
          {
            question: 'What filter removes the most microplastics?',
            answer:
              'Look for a certified NSF/ANSI 401 microplastics claim, whatever the filter type. No filter is certified for nanoplastics, and most reverse-osmosis models carry no microplastics claim (AquaTru does, at 96.48%). Among pitchers, PUR PLUS (99.8%), Brita Elite (99.6%) and the LifeStraw Home glass pitcher are certified. Clearly Filtered publishes a higher lab figure but is not certified for it, and ZeroWater makes no microplastic claim.',
          },
          {
            question: 'Does the Brita pitcher itself add microplastics?',
            answer:
              'Minimally. The reservoir is BPA-free plastic holding cold water at room temperature, the low-heat, low-acidity conditions where plastic sheds least. If you want to remove even that variable, Brita and others sell glass and stainless dispensers, or you can decant filtered water into a glass carafe.',
          },
          {
            question: 'Does boiling water remove microplastics instead?',
            answer:
              'Boiling can cause some microplastics to bind with limescale in hard water, which then settles out, but it does not reliably remove them and can fragment particles. It is not a substitute for filtration. Use an appropriate filter and store the water in glass or stainless steel.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'World Health Organization',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles and potential implications for human health',
            publisher: 'WHO',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
          {
            authors: 'NSF International',
            year: 2024,
            title: 'NSF/ANSI 401: Emerging Compounds/Incidental Contaminants (includes microplastics)',
            publisher: 'NSF',
            url: 'https://www.nsf.org/knowledge-library/nsf-ansi-401',
          },
          {
            authors: 'Cox KD, Covernton GA, Davies HL, et al.',
            year: 2019,
            title: 'Human consumption of microplastics',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b01517',
          },
          {
            authors: 'Zangmeister CD, Radney JG, Benkstein KD, Kalanyan B.',
            year: 2022,
            title: 'Common single-use consumer plastics release trillions of nanoparticles per litre into water',
            publisher: 'Environmental Science & Technology Letters',
            url: 'https://pubs.acs.org/doi/10.1021/acs.estlett.2c00232',
          },
          {
            authors: 'Yang Z, et al.',
            year: 2024,
            title: 'Drinking boiled tap water reduces human intake of nanoplastics and microplastics',
            publisher: 'Environmental Science & Technology Letters',
            url: 'https://pubs.acs.org/doi/10.1021/acs.estlett.4c00081',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
